import React, { Children, isValidElement } from 'react';
import PageHero from './PageHero';

// Shared layout for long-form legal pages. Renders a sticky table-of-contents
// on the left (desktop only) built from any `<Section id="..." heading="..." />`
// children, and the section bodies on the right.

export function Section({ id, number, heading, children }) {
  return (
    <section id={id} className="scroll-mt-24 py-6 first:pt-0">
      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-ink-900 flex items-start gap-3">
        {number != null && (
          <span className="shrink-0 text-brand-700 tabular-nums">{number}.</span>
        )}
        <span>{heading}</span>
      </h2>
      <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-ink-700">
        {children}
      </div>
    </section>
  );
}

// Sub-section for items like "9.1 Program Overview".
export function SubSection({ id, number, heading, children }) {
  return (
    <div id={id} className="scroll-mt-24 mt-6">
      <h3 className="text-base sm:text-lg font-semibold text-ink-900">
        {number ? `${number} ` : ''}{heading}
      </h3>
      <div className="mt-2 space-y-3">{children}</div>
    </div>
  );
}

export function P({ children }) {
  return <p className="text-[15px] leading-relaxed text-ink-700">{children}</p>;
}

export function UL({ children }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-[15px] leading-relaxed text-ink-700 marker:text-brand-700/70">
      {children}
    </ul>
  );
}

export function Notice({ children, tone = 'warning' }) {
  const cls = tone === 'warning'
    ? 'bg-accent-50 border-accent-200 text-ink-900'
    : 'bg-brand-50 border-brand-200 text-ink-900';
  return (
    <div className={`my-4 rounded-xl border px-5 py-4 text-sm leading-relaxed ${cls}`}>
      {children}
    </div>
  );
}

export default function LegalShell({
  eyebrow,
  title,
  intro,
  lastUpdated,
  version,
  governingLaw,
  children,
}) {
  // Build the ToC by scanning top-level Section children for { id, heading }.
  const tocItems = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === Section && child.props.id && child.props.heading) {
      tocItems.push({
        id: child.props.id,
        number: child.props.number,
        heading: child.props.heading,
      });
    }
  });

  return (
    <>
      <PageHero eyebrow={eyebrow} title={title} subtitle={intro} />

      <section className="bg-white">
        <div className="container-x py-12 sm:py-16">
          {/* Version / last-updated meta strip */}
          <div className="mb-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-500 pb-6 border-b border-ink-200">
            {version && (
              <span><span className="font-semibold text-ink-900">Version:</span> {version}</span>
            )}
            {lastUpdated && (
              <span><span className="font-semibold text-ink-900">Last updated:</span> {lastUpdated}</span>
            )}
            {governingLaw && (
              <span><span className="font-semibold text-ink-900">Governing law:</span> {governingLaw}</span>
            )}
          </div>

          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sidebar ToC (desktop only) */}
            {tocItems.length > 0 && (
              <aside className="hidden lg:block lg:col-span-3">
                <div className="sticky top-24">
                  <div className="text-xs uppercase tracking-[0.18em] text-ink-500 font-semibold mb-3">
                    On this page
                  </div>
                  <nav>
                    <ul className="space-y-1.5 text-sm">
                      {tocItems.map((t) => (
                        <li key={t.id}>
                          <a
                            href={`#${t.id}`}
                            className="block py-1 text-ink-600 hover:text-brand-700 transition-colors"
                          >
                            {t.number != null && (
                              <span className="text-ink-400 tabular-nums mr-1.5">{t.number}.</span>
                            )}
                            {t.heading}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </aside>
            )}

            {/* Body */}
            <article className={tocItems.length > 0 ? 'lg:col-span-9' : 'mx-auto max-w-3xl'}>
              <div className="divide-y divide-ink-100">
                {children}
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
